import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { SelectInput } from '../../partials/Inputs/Select'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { DatePicker } from '../../partials/Inputs/DatePicker'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@csinstruments/cs-react-theme/dist/components/DataTable/DataTable'
import { Box, Divider, Typography } from '@material-ui/core'
import {
  DataImportRequest,
  getFirstAndLastDateFromLeakagesFromRequests,
  processImport,
} from '../../../helpers/dataImport/dataImportService'
import { DataImportTarget, DatePickerDates, ImportedPath } from '../../../../interfaces/DataImport'
import {
  importCompaniesToTableRows,
  dataImportColumns,
  getCompanyNames,
  getBuildingNames,
  DataImportFilter,
} from './dataImportShared'
import { DataTableRow } from '@csinstruments/cs-react-theme/dist/components/DataTable/DataTableTypes'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import NotInterestedIcon from '@material-ui/icons/NotInterested'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import { paddingBottomActionBar, paddingTop, standardContentPadding } from '../../../../styles/Shared'
import { StandardConfirmationModal } from '../../partials/StandardConfirmationModal/StandardConfirmationModal'
import { useDataImport } from '../../../hooks/useDataImport'
import { FileSelectionImage } from '../../../helpers/dataImport/dataImportTypes'
import { ResultAlert } from '../../partials/ResultAlert'
import { FixedActionBar } from '@csinstruments/cs-react-theme/dist/components/FixedActionBar/FixedActionBar'
import { DataImportTargetSelection } from './DataImportTargetSelection'
import { StepperWizard } from '@csinstruments/cs-react-theme/dist/components/StepperWizard/StepperWizard'
import { DataImportFolderSelection } from './DataImportFolderSelection'
import { ProgressBar } from '../../partials/ProgressBar/ProgressBar'
import { usePermissions } from '../../../hooks/usePermissions'
import { PageHeading } from '../../partials/PageHeading'
import { descriptionContainer } from '@csinstruments/cs-react-theme/dist/components/ActionWithDescription/ActionWithDescription.styles'
import { Dropdown } from '../../partials/Dropdown/Dropdown'
import { DataImportJournalSelection } from './DataImportJournalSelection'
import { DataImportCompleted } from './DataImportCompleted'
import { useCurrentHardwareVersion } from '../../../hooks/useCurrentHardwareVersion'
import { useUpdateInfo } from '../../../hooks/useUpdateInfo'
import { Alert } from '@material-ui/lab'
import { removeLDUnsupportedCharacters } from '../../../helpers/dataExportHelpers'
import { isUniqueString } from '../../../helpers/utils'
import path from 'path'
import { GroupExplorer, NestedGroups } from 'app/components/partials/GroupsExplorer/GroupExplorer'
import { DisplayCompany, DisplayGroup } from 'interfaces/BusinessObjects'
import { collectSubGroupIds, convertToFileHierarchy } from 'app/helpers/convertToFileHierarchy'
import { useCompaniesInGroup } from 'app/hooks/companyHooks'
import { ActionWithDescription, CsButtonPrimary } from '@csinstruments/cs-react-theme'
import { TagAttribute, useGetTagAttributesQuery } from 'api/models'
import { getSelectedRealm } from 'app/helpers/manageRealms'
import { TagFilter } from 'app/components/partials/TagAttributes/TagAttributeFilter'

type DataImportInfo = { journalNames: string[]; textContents: string[] }
const actionButtonProps = [{}, { solid: true }]

export const DataImport: React.FC = () => {
  const { t } = useTranslation('common')
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [showSkip, setShowSkip] = useState<boolean>(false)
  const [showWarning, setShowWarning] = useState<boolean>(false)
  const [inconsistentData, setInconsistentData] = useState<boolean>(false)
  const [tableRows, setTableRows] = useState<DataTableRow[]>([])
  const [dataImportRequest, setDataImportRequest] = useState<DataImportRequest | undefined>()
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [filter, setFilter] = useState<DataImportFilter>({ company: [], building: '' })
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [importedPath, setImportedPath] = useState<ImportedPath>()
  const [dataImportError, setDataImportError] = useState<boolean>(false)
  const { importData, importDone, importProgress, error } = useDataImport(setImportedPath)
  const [selectionImages, setSelectionImages] = useState<FileSelectionImage[]>([])
  const [activeStep, setActiveStep] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [dataImportInfo, setDataImportInfo] = useState<DataImportInfo>({ journalNames: [], textContents: [] })
  const [selectedJournal, setSelectedJournal] = useState('')
  const permissions = usePermissions()
  const dataImportText = t('actions.dataImport')
  const [companyToBeImported, setCompanyToBeImported] = useState(0)
  const [targetForFirstCompany, setTargetForFirstCompany] = useState<DataImportTarget>({})
  const { major, minor } = useCurrentHardwareVersion()
  const [outdated, setOutdated] = useState(false)
  const [missingImages, setMisingImages] = useState(false)
  const [datePickerDates, setDatePickerDates] = useState<DatePickerDates>({ first: new Date(), last: new Date() })
  const [allGroups, setAllGroups] = useState<DisplayGroup[]>([])
  const [comps, setCompsList] = useState<DisplayCompany[]>([])
  const [allCompanies, setAllCompanies] = useState<DisplayCompany[]>([])
  const [initialGroups, setInitialGroups] = useState<DisplayGroup[]>([])
  const [selectedTags, setSelectedTags] = useState<TagAttribute[]>([])
  const [selGroupId, setSelGroupID] = useState<DisplayGroup>()
  const updateInfo = useUpdateInfo()
  const { groupCompanyData } = useCompaniesInGroup()

  const [groupsList, companiesList] = groupCompanyData
  //const { groupHierarchy } = convertToFileHierarchy(groupsList, companiesList)
  const { data } = useGetTagAttributesQuery({
    variables: {
      realmName: getSelectedRealm(),
    },
    skip: !getSelectedRealm(),
  })

  // useEffect(() => {
  //   if (dataImportRequest) {
  //     const newTableRows = importCompaniesToTableRows(dataImportRequest.companies ?? [], filter)
  //     setTableRows(newTableRows)

  //     const leakages = dataImportRequest.companies
  //       .map((c) => c.projects)
  //       .flat()
  //       .map((p) => p.leakages)
  //       .flat()

  //     let outdated = false
  //     const isJson = /\.json$/i.test(selectedJournal || '')

  //     if (!isJson) {
  //       if (leakages.length) {
  //         const sn = leakages[0].sn
  //         const sw = leakages[0].sw

  //         const currentForSn = updateInfo.find((ui) => ui.sn === `${sn}`)
  //         const current = parseFloat(`${major}.${minor}`)

  //         if (!currentForSn || parseFloat(`${currentForSn.major}.${currentForSn.minor}`) < current) {
  //           outdated = sw < current || isNaN(sw)
  //         }
  //         if (!sn) {
  //           outdated = true
  //         }
  //       }
  //       setOutdated(outdated)
  //     }
  //   }
  // }, [dataImportRequest, filter, major, minor, updateInfo, selectedJournal])
  useEffect(() => {
    if (dataImportRequest) {
      const newTableRows = importCompaniesToTableRows(dataImportRequest.companies ?? [], filter)
      setTableRows(newTableRows)
    }
  }, [dataImportRequest, filter, selectedJournal])

  const reset = useCallback((): void => {
    setTableRows([])
    setSelectedRows([])
    setActiveStep(0)
  }, [])

  useEffect(() => {
    if (importDone) {
      setLoading(false)
      if (inconsistentData) {
        setShowWarning(true)
      } else {
        setShowSuccess(true)
      }
    }
  }, [importDone, inconsistentData])
  useEffect(() => {
    if (error && error) {
      setDataImportError(true)
    }
  }, [error])
  useEffect(() => {
    if (groupsList.length) {
      setAllGroups(groupsList)
    }
  }, [groupsList])
  // useEffect(() => {
  //   if (!initialGroups.length && groupHierarchy.length) {
  //     setInitialGroups(groupHierarchy)
  //   }
  // }, [groupHierarchy])
  useEffect(() => {
    if (!selGroupId && companiesList.length) {
      setAllCompanies(companiesList)
      setCompsList(companiesList)
    }
  }, [selGroupId, companiesList])
  // useEffect(() => {
  //   if (selGroupId && allCompanies) {
  //     const subGroupIds: string[] = []
  //     const ids = collectSubGroupIds(groupHierarchy, subGroupIds, selGroupId.id)
  //     //console.log({ ids }, { allCompanies })
  //     const filteredCompaniesByGroup = allCompanies.filter((c) => c?.groupId && ids.includes(c.groupId))
  //     setCompsList(filteredCompaniesByGroup)
  //   }
  // }, [selGroupId])

  useEffect(() => {
    if (activeStep === 0) {
      setDataImportRequest({ companies: [], error: false })
      setCompanyToBeImported(0)
      reset()
    } else if (activeStep === 2) {
      processImport(
        dataImportInfo.textContents[dataImportInfo.journalNames.findIndex((jn) => jn === selectedJournal)],
        dataImportText,
        setDataImportRequest,
        selectedJournal,
      )
    }
  }, [
    reset,
    activeStep,
    setDataImportRequest,
    dataImportInfo.journalNames,
    dataImportInfo.textContents,
    dataImportText,
    selectedJournal,
  ])

  useEffect(() => {
    if (activeStep === 2) {
      const firstAndLastDateFromLeakages = getFirstAndLastDateFromLeakagesFromRequests(dataImportRequest)
      setDatePickerDates(firstAndLastDateFromLeakages)
      //console.log({ dataImportRequest, selectionImages })
      if (dataImportRequest) {
        /*** May show certain folder or image missing in the warning ***/
        const allImageNamesArray = selectionImages.map((image) => ({
          img: path.basename(image.name),
          imgPath: image.name.split(path.sep)[image.name.split(path.sep).length - 2],
        }))
        // console.log({ allImageNamesArray })
        const leakImageNamesArray = dataImportRequest.companies.flatMap((c) =>
          c.projects.flatMap((project) =>
            project.leakages.map((leak) => ({ img: leak.imgName, imgPath: leak.imgPath })),
          ),
        )
        console.log({ leakImageNamesArray })

        const someImageMissing = leakImageNamesArray.length > allImageNamesArray.length
        //console.log({ someImageMissing, leakImageNamesArray, allImageNamesArray })
        setMisingImages(someImageMissing)
      }
    }
  }, [activeStep, dataImportRequest, selectionImages])

  useEffect(() => {
    if (activeStep === 3) {
      setDataImportRequest((r) => {
        return r
          ? {
              ...r,
              companies:
                r?.companies.filter((c) => c.projects[0].leakages.filter((l) => selectedRows.includes(l.uid)).length) ||
                [],
              journalPath: selectedJournal,
            }
          : undefined
      })
    }
  }, [selectedRows, activeStep, setDataImportRequest, selectedJournal])

  const hasPermission = useMemo(() => {
    return permissions.importProjects
  }, [permissions])

  const hasAddPermission = useMemo(() => {
    return permissions.addCompanies
  }, [permissions])

  const handleCompanySelection = useCallback(
    (values: string[]): void => {
      const newFilter = filter
      newFilter['company'] = values
      setFilter(newFilter)
      const newTableRows = importCompaniesToTableRows(dataImportRequest?.companies ?? [], newFilter)
      setTableRows(newTableRows)
    },
    [dataImportRequest?.companies, filter],
  )

  const handleSelection = useCallback(
    (type: 'company' | 'building' | 'dateFrom' | 'dateUntil', value: string | Date): void => {
      const newFilter = filter
      if (type === 'building' && typeof value === 'string') {
        newFilter[type] = value
        setFilter(newFilter)
      } else if ((type === 'dateFrom' || type === 'dateUntil') && value instanceof Date) {
        newFilter[type] = value
        setFilter(newFilter)
      }
      const newTableRows = importCompaniesToTableRows(dataImportRequest?.companies ?? [], newFilter)

      setTableRows(newTableRows)
    },
    [dataImportRequest?.companies, filter],
  )

  const onSelectionChanged = useCallback((ids: string[]): void => {
    setSelectedRows(ids)
  }, [])

  const importAction = useCallback(() => {
    if (!dataImportRequest) {
      return
    }
    setShowSuccess(false)
    setShowWarning(false)
    setLoading(true)
    const request = Object.assign({}, dataImportRequest)
    request.uids = selectedRows
    request.images = selectionImages
    request.companies = [dataImportRequest.companies[companyToBeImported]]

    if (request && request.companies.length) {
      for (const company of request.companies) {
        if (company.projects.length) {
          for (const project of company.projects) {
            if (project.leakages.length) {
              if (!project.unitV) {
                const uniqueUnitV = project.leakages.map((leakage) => leakage.unitV).filter(isUniqueString)
                if (uniqueUnitV.length === 1) {
                  project.unitV = uniqueUnitV[0]
                } else {
                  setInconsistentData(true)
                }
              }
            }
          }
        }
      }
    }
    //console.log({ request })
    setCompanyToBeImported((i) => i + 1)

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    importData(request)
      .then(() => {
        console.log('imported')
      })
      .catch((err) => {
        console.error(err)
      })
  }, [dataImportRequest, selectedRows, selectionImages, companyToBeImported, importData])

  const skipImport = useCallback(() => {
    setCompanyToBeImported((i) => i + 1)
    setShowSkip(true)
  }, [])

  const selectionChanged = useCallback(
    (selection: DataImportTarget) => {
      setDataImportRequest((prevState: DataImportRequest | undefined): DataImportRequest | undefined => {
        return !prevState
          ? undefined
          : {
              ...prevState,
              target: {
                ...prevState.target,
                ...selection,
                //groupId: !selGroupId?.id ? selection.groupId : selGroupId.id ?? '',
              },
            }
      })
      if (companyToBeImported == 0) {
        setTargetForFirstCompany(selection)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyToBeImported, dataImportRequest?.companies],
  )

  const closeModal = useCallback(() => setModalOpen(false), [])
  const actionButtonDisabled = useMemo(() => {
    const disabledIf =
      activeStep === 0 ||
      (!selectedRows.length && activeStep === 2) ||
      (importDone && activeStep === 3 && companyToBeImported >= (dataImportRequest?.companies.length || 1000))
    if (activeStep === 2) {
      return [false, disabledIf]
    }
    if (activeStep === 3) {
      const disableImport = !hasAddPermission && !dataImportRequest?.target?.companyName
      return [false, disableImport]
    }
    if (activeStep === 3 && importDone) {
      return [false, importProgress ?? loading]
    }
    return [activeStep <= 1 ? disabledIf : disabledIf, disabledIf]
  }, [
    activeStep,
    selectedRows.length,
    importDone,
    companyToBeImported,
    dataImportRequest?.companies.length,
    dataImportRequest?.target?.companyName,
    importProgress,
    loading,
    hasAddPermission,
  ])
  const actionButtonActions = useMemo(() => {
    if (activeStep === 1 || activeStep === 2) {
      return [() => setActiveStep((as) => as - 1), () => setActiveStep((as) => as + 1)]
    }
    if (activeStep === 3 && !error) {
      return [skipImport, importAction]
    }
    return [() => setActiveStep((as) => as + 1)]
  }, [activeStep, importAction, skipImport, error])
  //console.log('company', companyToBeImported, 'companiesImport.length', dataImportRequest?.companies.length)
  const actionButtonLabel = useMemo(() => {
    if (activeStep === 1 || activeStep === 2) {
      return ['actions.back', 'actions.continue']
    }
    if (activeStep === 3) {
      return ['actions.skip', 'actions.import']
    }
    return ['actions.continue']
  }, [activeStep])

  const actionButtonIcons = useMemo(() => {
    if (activeStep === 1 || activeStep === 2) {
      return [NavigateBeforeIcon, NavigateNextIcon]
    }
    if (activeStep === 3) {
      return [NotInterestedIcon, NavigateNextIcon]
    }
    return [NavigateNextIcon]
  }, [activeStep])
  const stepperDisabled = useMemo(
    () => [
      false,
      !dataImportInfo || !dataImportInfo.journalNames.length,
      !dataImportRequest || !dataImportRequest.companies.length,
      !dataImportRequest || !dataImportRequest.companies.length || !error,
    ],
    [dataImportInfo, dataImportRequest, error],
  )
  const stepperLabels = useMemo(
    () => ['actions.chooseXml', 'dataImport.chooseXML', 'dataImport.chooseLeakages', 'dataImport.chooseTarget'],
    [],
  )
  const outdatedLabel = useMemo(() => t('updateOverview.outdatedInfo'), [t])
  const dataImportInfoUpdateAction = useCallback((journalNames: string[], textContents: string[]) => {
    setActiveStep(1)
    console.log({ journalNames })
    setDataImportInfo({ textContents: textContents.map((tc) => removeLDUnsupportedCharacters(tc)), journalNames })
    setSelectedJournal(journalNames.length ? journalNames[0] : '')
  }, [])

  const companyNames = useMemo(
    () => getCompanyNames(dataImportRequest?.companies ?? []),
    [dataImportRequest?.companies],
  )

  const targetCompanyName = useMemo(
    () => dataImportRequest?.companies[companyToBeImported]?.name || '',
    [dataImportRequest?.companies, companyToBeImported],
  )
  // const handleTargetGroup = (groupID: DisplayGroup) => {
  //   setSelGroupID(groupID)
  //   setTargetForFirstCompany({ groupId: groupID.id })
  //   selectionChanged({ groupId: groupID.id })
  // }

  // const filterReset = () => {
  //   setSelGroupID(undefined)
  //   selectionChanged({ companyName: undefined, groupId: undefined, projectId: undefined })
  // }
  const tagOptions = useMemo(() => {
    if (!data?.getTagAttributes.length) return []
    return data.getTagAttributes
  }, [data?.getTagAttributes])

  const handleTagFilter = (ids: string[]) => {
    const resetList = tagOptions.filter((tg) => ids.includes(tg.ID))
    setSelectedTags(resetList)
    const tmpCompanyIDS = resetList
      .map((ta) => ta.attributes)
      .flat()
      .map((aid) => aid.attributeID)
    const filterdComps = allCompanies.filter((c) => c._id && tmpCompanyIDS.includes(c?._id))
    setCompsList(filterdComps.length ? filterdComps : allCompanies)
  }

  return (
    <div style={{ ...paddingBottomActionBar, ...paddingTop }}>
      <ResultAlert alertText={'dataImport.success'} showAlert={showSuccess} modifyShowAlert={setShowSuccess} />
      <ResultAlert
        alertText={'dataImport.failure'}
        severity="error"
        showAlert={dataImportError}
        autoHideDuration={6000}
        modifyShowAlert={setDataImportError}
      />
      <ProgressBar show={importProgress ?? loading} />
      <ResultAlert
        severity="warning"
        alertText={'dataImport.inconsistentData'}
        autoHideDuration={6000}
        showAlert={showWarning}
        modifyShowAlert={setShowWarning}
      />

      <ResultAlert
        severity="warning"
        alertText={'dataImport.skip'}
        showAlert={showSkip}
        modifyShowAlert={setShowSkip}
      />
      <StandardConfirmationModal handleConfirm={reset} open={modalOpen} handleClose={closeModal} />
      <FixedActionBar
        disabled={actionButtonDisabled}
        buttonProps={actionButtonProps}
        actions={actionButtonActions}
        labels={actionButtonLabel}
        icons={actionButtonIcons}
      />
      <StepperWizard
        step={activeStep}
        disabled={stepperDisabled}
        labels={stepperLabels}
        onStepClicked={setActiveStep}
      />
      <div style={standardContentPadding}>
        {activeStep === 0 && (
          <DataImportFolderSelection
            permitted={hasPermission}
            setSelectionImages={setSelectionImages}
            setDataImportInfo={dataImportInfoUpdateAction}
          />
        )}

        {activeStep === 1 && (
          <DataImportJournalSelection
            journalNames={dataImportInfo.journalNames}
            selectedJournal={selectedJournal}
            handleSelectJournal={(n) => {
              console.log({ value: dataImportInfo.journalNames[n] })
              setSelectedJournal(dataImportInfo.journalNames[n])
            }}
          />
        )}

        {activeStep === 2 && (
          <Grid container justifyContent="flex-start" spacing={4}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <>
                <Grid item xs={12}>
                  <ProgressBar show={loading} />
                </Grid>
                <Grid item container spacing={2}>
                  {missingImages && (
                    <Grid item xs={12}>
                      <Alert severity="warning" elevation={2}>
                        <Typography gutterBottom align="center">
                          {t('dataImport.missingImages')}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  {outdated && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{outdatedLabel}</Alert>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Grid container alignItems={'flex-end'} spacing={2}>
                    <Grid item xs={3}>
                      <Dropdown
                        valueSelected={handleCompanySelection}
                        label={'objects.company'}
                        values={companyNames}
                      />
                    </Grid>
                    <SelectInput
                      changeHandler={(s: string) => handleSelection('building', s)}
                      label={'objects.building'}
                      xs={3}
                      text={getBuildingNames(dataImportRequest?.companies ?? [])}
                    />
                    <DatePicker
                      xs={3}
                      label={'dataImport.periodFrom'}
                      onChange={(d) => {
                        handleSelection('dateFrom', d)
                      }}
                      preselect={datePickerDates.first}
                    />
                    <DatePicker
                      xs={3}
                      label={'dataImport.periodUntil'}
                      onChange={(d) => {
                        handleSelection('dateUntil', d)
                      }}
                      preselect={datePickerDates.last}
                    />
                  </Grid>
                </Grid>
              </>
            </MuiPickersUtilsProvider>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DataTable
                    allowedActions={{ checkable: true }}
                    rows={tableRows}
                    columns={dataImportColumns}
                    onSelectionChanged={onSelectionChanged}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {activeStep === 3 && (
          <>
            {(!importDone || companyToBeImported < (dataImportRequest?.companies.length || 1000)) && (
              <Grid container alignContent="center" alignItems="flex-start">
                <Grid item xs={7} container spacing={1} alignContent="center" alignItems="flex-start">
                  <Grid item xs={8}>
                    <Box pr={4}>
                      <ActionWithDescription
                        heading={
                          dataImportRequest && dataImportRequest.companies.length > companyToBeImported
                            ? dataImportRequest.companies[companyToBeImported].name
                            : ''
                        }
                        description={t('dataImport.targetDescription')}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={10} />

                  <Grid item container xs={11} alignContent="center">
                    <DataImportTargetSelection
                      companyName={targetCompanyName}
                      handleSelectionChanged={selectionChanged}
                      targetGroupId={targetForFirstCompany.groupId}
                      comps={comps}
                      canCreateCompany={hasAddPermission}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={3} container direction="column">
                  <TagFilter
                    filterHandler={handleTagFilter}
                    tagFilterAttributeOptions={tagOptions}
                    tagFilterValues={selectedTags}
                  />
                  {/* {initialGroups.length ? (
                    <Grid item container alignContent="flex-start" alignItems="flex-start">
                      <Grid item xs={6}>
                        <CsButtonPrimary fullwidth title="Reset Filter" disabled={!selGroupId} onClick={filterReset}>
                          {t('actions.viewAllCompanies')}
                        </CsButtonPrimary>
                      </Grid>

                      <NestedGroups
                        selectedGroup={selGroupId}
                        handleSelect={handleTargetGroup}
                        groups={initialGroups}
                        filterable
                      />
                    </Grid>
                  ) : null} */}
                </Grid>
                {/* <Grid item xs={6} container spacing={2}>
                  <Grid item xs={12} />
                  <DataImportTargetSelection
                    companyName={targetCompanyName}
                    handleSelectionChanged={selectionChanged}
                    targetGroupId={targetForFirstCompany.groupId}
                    comps={comps}
                  />
                </Grid> */}
              </Grid>
            )}
            {importDone && companyToBeImported >= (dataImportRequest?.companies.length || 1000) && (
              <DataImportCompleted
                additionalDataAction={reset}
                project={importedPath?.projectId}
                projectIsID={!!importedPath?.projectId}
                groupID={importedPath?.groupId}
                companyName={importedPath?.companyId}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
